import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useApi } from "~hooks/apiHooks/useApi";
import type { FixtureResponse } from "~types/fixture";
import type { EditFixturePageModel } from "../types/editFixturePageModel";
import { transformFixtureResponse } from "../utilities";

const include = ["entities", "seasons", "persons", "uniforms", "seasonStages", "seasonPools", "seasonSeries", "fixtureProfiles"];

export const getGetFixtureQueryKey = ({
  sport,
  organizationId,
  fixtureId,
  seasonId,
}: {
  sport: string;
  organizationId: string;
  fixtureId: string;
  seasonId: string;
}) => ["fixture", sport, organizationId, fixtureId, seasonId, ...include];

export const useGetFixture = (options: Omit<UseQueryOptions<EditFixturePageModel, unknown>, "queryKey" | "queryFn"> = {}) => {
  const request = useApi();
  const { sport, organizationId, fixtureId, seasonId } = useParams();

  if (!sport || !organizationId || !fixtureId || !seasonId) {
    console.error({ sport, organizationId, fixtureId, seasonId });
    throw new Error("Missing required params");
  }

  return useQuery<EditFixturePageModel, unknown>({
    queryKey: getGetFixtureQueryKey({
      sport,
      organizationId,
      fixtureId,
      seasonId,
    }),
    queryFn: () =>
      request<FixtureResponse>(`${sport}/o/${organizationId}/fixtures/${fixtureId}?include=${include.join(",")}`).then(
        transformFixtureResponse,
      ),
    enabled: Boolean(fixtureId && organizationId && sport),
    staleTime: 1000,
    ...options,
  });
};
